import { useState, useCallback, useEffect, useRef } from 'react';
import { v4 as uuidv4 } from 'uuid';
import { ResolvedMatch, Employee, AttendeePriority } from '../interfaces/interface';
import { logger } from '../../../../logger';
import { EmployeeService } from '../services/EmployeeService';
import { EmployeeSettingsService } from '../services/EmployeeSettingsService';
import {
  sortAttendees,
  isDuplicateEmployee,
  findDuplicateEmails as findDuplicates,
  getExistingEmails as getEmails,
  hasUnresolvedNames as hasUnresolved
} from '../utils/attendeeUtils';

export const useAttendeeManagement = (alias: string) => {
  const [resolvedAttendees, setResolvedAttendees] = useState<ResolvedMatch[]>([]);

  // ── Favorites (merged from useFavorites) ─────────────────────────────
  const [favoriteLoginNames, setFavoriteLoginNames] = useState<Set<string>>(new Set());
  const isMounted = useRef(true);
  const pendingSaves = useRef(new Set<string>());

  useEffect(() => {
    isMounted.current = true;
    if (!alias) return;
    EmployeeSettingsService.getFavorites(alias)
      .then((loginNames) => {
        if (isMounted.current) setFavoriteLoginNames(new Set(loginNames));
      })
      .catch((error) => {
        logger.error('Failed to load favorites', { error, alias });
      });
    return () => {
      isMounted.current = false;
    };
  }, [alias]);

  const isFavorite = useCallback((loginName: string): boolean => favoriteLoginNames.has(loginName), [favoriteLoginNames]);

  // Reconcile isFavorite flags whenever favorites or attendees change
  const attendeeCount = resolvedAttendees.length;
  useEffect(() => {
    setResolvedAttendees((prev) => {
      if (prev.length === 0) return prev;
      let changed = false;
      const updated = prev.map((a) => {
        const login = a.selectedEmployee?.login_name;
        if (!login) return a;
        const shouldBeFav = favoriteLoginNames.has(login);
        if (a.isFavorite !== shouldBeFav) {
          changed = true;
          return { ...a, isFavorite: shouldBeFav };
        }
        return a;
      });
      return changed ? updated : prev;
    });
  }, [favoriteLoginNames, attendeeCount]);

  const toggleFavorite = useCallback(
    (loginName: string) => {
      if (!loginName || !alias || pendingSaves.current.has(loginName)) return;
      pendingSaves.current.add(loginName);
      setFavoriteLoginNames((prev) => {
        const updated = new Set(prev);
        if (updated.has(loginName)) updated.delete(loginName);
        else updated.add(loginName);
        EmployeeSettingsService.saveFavorites(alias, Array.from(updated))
          .catch((error) => {
            logger.error('Failed to save favorites', { error, alias, loginName });
            setFavoriteLoginNames(prev); // Rollback to previous state
          })
          .finally(() => {
            pendingSaves.current.delete(loginName);
          });
        return updated;
      });
    },
    [alias]
  );

  /**
   * Fetches complete employee data from PAPI when the assistant ID is missing.
   * This ensures we have all employee fields including the EA reference.
   */
  const fetchCompleteEmployeeData = useCallback(async (employee: Employee): Promise<Employee> => {
    if (!employee.employee_assistant_employee_id && employee.login_name) {
      try {
        const cached = await EmployeeService.getEmployeeByLogin(employee.login_name);

        if (!cached) {
          return employee;
        }

        return cached;
      } catch (error) {
        logger.error('Failed to fetch complete employee data', { error });
      }
    }
    return employee;
  }, []);

  // Ref to track current attendees for duplicate checks without stale closures
  const resolvedAttendeesRef = useRef<ResolvedMatch[]>([]);
  useEffect(() => {
    resolvedAttendeesRef.current = resolvedAttendees;
  }, [resolvedAttendees]);

  const handleAddAttendee = useCallback(
    async (employee: Employee, priority: AttendeePriority, extraFields?: Pick<ResolvedMatch, 'proxyForLogin' | 'proxyForName'>) => {
      if (isDuplicateEmployee(employee, resolvedAttendeesRef.current)) {
        return;
      }

      const newId = `attendee-${uuidv4()}`;

      // Add immediately with basic data (optimistic) for instant UI feedback
      const placeholderAttendee: ResolvedMatch = {
        inputName: employee.employee_name || employee.login_name,
        matches: [employee],
        selectedEmployee: employee,
        status: 'exact',
        priority,
        id: newId,
        resolvedEmail: employee.employee_internal_email_address || `${employee.login_name}@amazon.com`,
        isFavorite: isFavorite(employee.login_name),
        ...extraFields
      };
      setResolvedAttendees((prev) => sortAttendees([...prev, placeholderAttendee]));

      // Enrich in background (PAPI calls for full employee data + EA lookup)
      try {
        const enrichedEmployee = await fetchCompleteEmployeeData(employee);
        if (!enrichedEmployee) {
          logger.error('Failed to enrich employee data');
          return;
        }

        const selectedExecutiveAssistant = await EmployeeService.findExecutiveAssistant(enrichedEmployee);

        setResolvedAttendees((prev) =>
          sortAttendees(
            prev.map((a) =>
              a.id === newId
                ? {
                    ...a,
                    inputName: enrichedEmployee.employee_name || a.inputName,
                    matches: [enrichedEmployee],
                    selectedEmployee: enrichedEmployee,
                    selectedExecutiveAssistant,
                    resolvedEmail: enrichedEmployee.employee_internal_email_address || a.resolvedEmail
                  }
                : a
            )
          )
        );
      } catch (error) {
        logger.error('Failed to enrich attendee data in background', { error });
      }
    },
    [fetchCompleteEmployeeData, isFavorite]
  );

  const handleAttendeeUpdate = useCallback(
    async (id: string, updates: Partial<ResolvedMatch>) => {
      if (updates.selectedEmployee) {
        const enrichedEmployee = await fetchCompleteEmployeeData(updates.selectedEmployee);

        if (!enrichedEmployee) {
          logger.error('Failed to enrich employee data during update');
          return;
        }

        try {
          const selectedExecutiveAssistant = await EmployeeService.findExecutiveAssistant(enrichedEmployee);

          updates = {
            ...updates,
            selectedEmployee: enrichedEmployee,
            selectedExecutiveAssistant
          };
        } catch (error) {
          logger.warn('Failed to lookup executive assistant for manual employee selection', { error });
        }
      }

      setResolvedAttendees((prev) => {
        const updated = prev.map((attendee) => (attendee.id === id ? { ...attendee, ...updates } : attendee));
        return updated;
      });
    },
    [fetchCompleteEmployeeData]
  );

  const handleAttendeeRemove = useCallback((id: string) => {
    setResolvedAttendees((prev) => prev.filter((attendee) => attendee.id !== id));
  }, []);

  const handleAddProxy = useCallback(
    async (principalId: string, proxyEmployee: Employee) => {
      const principal = resolvedAttendeesRef.current.find((a) => a.id === principalId);
      if (!principal?.selectedEmployee) return;
      if (isDuplicateEmployee(proxyEmployee, resolvedAttendeesRef.current)) return;

      const principalLogin = principal.selectedEmployee.login_name;
      const principalName = principal.selectedEmployee.employee_name || principal.inputName;

      // Remove existing proxy for this principal (if replacing)
      setResolvedAttendees((prev) => prev.filter((a) => a.proxyForLogin?.toLowerCase() !== principalLogin.toLowerCase()));

      // Change the principal to optional
      setResolvedAttendees((prev) => prev.map((a) => (a.id === principalId ? { ...a, priority: AttendeePriority.Optional } : a)));

      const newId = `attendee-${uuidv4()}`;
      const placeholderProxy: ResolvedMatch = {
        inputName: proxyEmployee.employee_name || proxyEmployee.login_name,
        matches: [proxyEmployee],
        selectedEmployee: proxyEmployee,
        status: 'exact',
        priority: AttendeePriority.Optional,
        id: newId,
        resolvedEmail: proxyEmployee.employee_internal_email_address || `${proxyEmployee.login_name}@amazon.com`,
        isFavorite: isFavorite(proxyEmployee.login_name),
        proxyForLogin: principalLogin,
        proxyForName: principalName
      };
      setResolvedAttendees((prev) => {
        // Insert proxy right after the principal without sorting
        const idx = prev.findIndex((a) => a.id === principalId);
        const result = [...prev];
        result.splice(idx + 1, 0, placeholderProxy);
        return result;
      });

      // Enrich in background
      try {
        const enrichedEmployee = await fetchCompleteEmployeeData(proxyEmployee);
        if (!enrichedEmployee) return;
        const selectedExecutiveAssistant = await EmployeeService.findExecutiveAssistant(enrichedEmployee);

        setResolvedAttendees((prev) =>
          prev.map((a) =>
            a.id === newId
              ? {
                  ...a,
                  inputName: enrichedEmployee.employee_name || a.inputName,
                  matches: [enrichedEmployee],
                  selectedEmployee: enrichedEmployee,
                  selectedExecutiveAssistant,
                  resolvedEmail: enrichedEmployee.employee_internal_email_address || a.resolvedEmail
                }
              : a
          )
        );
      } catch (error) {
        logger.error('Failed to enrich proxy attendee data', { error });
      }
    },
    [fetchCompleteEmployeeData, isFavorite]
  );

  const replaceDocumentAttendees = useCallback(
    (newDocumentAttendees: ResolvedMatch[]) => {
      setResolvedAttendees((prevAttendees) => {
        const manualAttendees = prevAttendees.filter((attendee) => attendee.id.startsWith('attendee-'));

        const filteredDocumentAttendees = newDocumentAttendees
          .filter((docAttendee) => !isDuplicateEmployee(docAttendee.selectedEmployee || ({} as Employee), manualAttendees))
          .map((a) => ({ ...a, isFavorite: isFavorite(a.selectedEmployee?.login_name || '') }));

        const combinedAttendees = [...manualAttendees, ...filteredDocumentAttendees];
        return sortAttendees(combinedAttendees);
      });
    },
    [isFavorite]
  );

  const getExistingEmails = useCallback(() => {
    return getEmails(resolvedAttendees);
  }, [resolvedAttendees]);

  const getRequiredAttendees = useCallback(() => {
    return resolvedAttendees.filter((a) => a.priority === 'required');
  }, [resolvedAttendees]);

  const getOptionalAttendees = useCallback(() => {
    return resolvedAttendees.filter((a) => a.priority === 'optional');
  }, [resolvedAttendees]);

  const findDuplicateEmails = useCallback(() => {
    return findDuplicates(resolvedAttendees);
  }, [resolvedAttendees]);

  const hasUnresolvedNames = useCallback(() => {
    return hasUnresolved(resolvedAttendees);
  }, [resolvedAttendees]);

  const resortAttendees = useCallback(() => {
    setResolvedAttendees((prev) => sortAttendees([...prev]));
  }, []);

  return {
    resolvedAttendees,
    handleAddAttendee,
    handleAttendeeUpdate,
    handleAttendeeRemove,
    handleAddProxy,
    replaceDocumentAttendees,
    getExistingEmails,
    getRequiredAttendees,
    getOptionalAttendees,
    findDuplicateEmails,
    hasUnresolvedNames,
    setResolvedAttendees,
    resortAttendees,
    favoriteLoginNames,
    isFavorite,
    toggleFavorite,
    refreshFavorites: useCallback(async () => {
      if (!alias) return;
      try {
        const loginNames = await EmployeeSettingsService.getFavorites(alias);
        if (isMounted.current) setFavoriteLoginNames(new Set(loginNames));
      } catch (error) {
        logger.error('Failed to refresh favorites', { error, alias });
      }
    }, [alias])
  };
};
